import React from 'react';
import { Shield, Loader2 } from 'lucide-react';
import type { ButtonVariant, ButtonSize } from '@/types';
import { FS, PASO, useTema, type CasaTema } from '@components/M02/casaTema';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** md (10/16, letra 13) y sm (7/12, letra 12) — las DOS medidas del diseño (`BotonIng`). `xs` y `lg` ya no existen como
   *  medida propia: son ALIAS de `sm` y `md` (I-244; el diseño no dibuja un tercero). */
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  /** El tema de la casa. Sin él, el botón se viste con `useTema()` (el de la casa si hay proveedor, el claro si no):
   *  UNA sola forma, no hay botón «sin tema» (I-244). Se conserva para quien lo pasa a mano. */
  tema?: CasaTema;
  /** Si viene, el botón se ve, queda deshabilitado y CONTESTA por qué (title) al pasar el mouse o el foco
   *  — ninguna acción termina muda. Se pinta con el escudo de la casa. */
  razon?: string | null;
  /** ALIAS sin efecto (I-244): el peligro YA es siempre el rojo SÓLIDO (D-2026-09-29-E / D-2026-09-30-E). Se conserva
   *  para no romper a quien lo escribía. */
  solido?: boolean;
  /** Botón de SOLO ícono (✕, «i», «?», papelera): cuadrado 30 (md) o 24 (sm), sin relleno de texto, con borde `lineSoft`
   *  (con `variant="ghost"`, sin borde). El valor ES el nombre accesible (`aria-label`): un ícono sin nombre no existe. */
  soloIcono?: string;
}

/** El botón de la casa — `handoff_claude_ingresos_vestido/casa-ingresos-vestido.jsx` (`BotonIng`, 163-183) y su README.
 *  primary = el fuerte (btnBg/btnFg, 700) · secondary = transparente con borde inputLine (600) · ghost = sin borde ·
 *  danger = el suave del vestido (dangerSoft/dangerLine, `jsx:175`); con `solido` es el confirmador destructivo en rojo SÓLIDO
 *  (dangerSolid + dangerFg, 700) — D-2026-09-29-E / D-2026-09-30-E: la decisión de Mau cubre a los confirmadores, no a todo `danger`. */
export const Button: React.FC<ButtonProps> = ({
  tema, variant = 'primary', size = 'md', loading = false, razon, solido, disabled, icon, iconPosition = 'left',
  fullWidth, children, className, style, type = 'button', soloIcono, ...props
}) => {
  const delaCasa = useTema();
  const T = tema ?? delaCasa;
  const chico = size === 'sm' || size === 'xs';
  const base: React.CSSProperties = {
    display: fullWidth ? 'flex' : 'inline-flex', width: fullWidth ? '100%' : undefined, alignItems: 'center', justifyContent: 'center',
    padding: chico ? '7px 12px' : '10px 16px', borderRadius: 10, fontSize: chico ? FS.boton : PASO.s13, fontFamily: 'inherit', whiteSpace: 'nowrap',
  };
  if (razon) {
    return (
      // `aria-disabled` y NO `disabled`: un botón `disabled` no recibe eventos del mouse y el `title` con el
      // motivo no se ve (Firefox) — ninguna acción termina muda. El clic se bloquea aquí, el foco se conserva.
      <button {...props} type={type} aria-disabled="true" title={razon} {...(soloIcono ? { 'aria-label': soloIcono } : {})}
        onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); }} className={className} style={{
        ...base, gap: 6, border: `1px solid ${T.line}`, background: T.chip, color: T.fg3,
        fontWeight: 600, cursor: 'not-allowed', ...style,
      }}>
        {icon ?? <Shield size={12} color={T.fg3} />}{children}
      </button>
    );
  }
  const fuerte = variant === 'primary';
  const malo = variant === 'danger';
  const rojoSolido = malo && !!solido;
  const rellenoRojo = T.dangerSolid ?? T.danger;
  const fondo = rojoSolido ? rellenoRojo : malo ? T.dangerSoft : fuerte ? T.btnBg : 'transparent';
  const letra = rojoSolido ? (T.dangerFg ?? T.btnFg) : malo ? T.danger : fuerte ? T.btnFg : T.fg2;
  const borde = rojoSolido ? rellenoRojo : malo ? T.dangerLine : fuerte ? T.btnBg : variant === 'ghost' ? 'transparent' : T.inputLine;
  const apagado = !!disabled && !loading;
  if (soloIcono) {
    const lado = chico ? 24 : 30;
    return (
      <button {...props} type={type} aria-label={soloIcono} disabled={disabled || loading} className={className} style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: lado, height: lado, padding: 0, flexShrink: 0,
        borderRadius: chico ? 7 : 8, border: `1px solid ${variant === 'ghost' ? 'transparent' : T.lineSoft}`, background: 'transparent',
        color: T.fg3, fontSize: PASO.s15, lineHeight: 1, fontFamily: 'inherit',
        cursor: apagado ? 'not-allowed' : 'pointer', opacity: apagado ? 0.5 : undefined, ...style,
      }}>
        {children}
      </button>
    );
  }
  return (
    <button {...props} type={type} disabled={disabled || loading} className={className} style={{
      ...base, gap: 7, border: `1px solid ${borde}`, background: fondo, color: letra,
      fontWeight: fuerte || rojoSolido ? 700 : 600,
      cursor: loading ? 'progress' : apagado ? 'not-allowed' : 'pointer', opacity: apagado ? 0.5 : undefined, ...style,
    }}>
      {loading && <Loader2 size={12} className="animate-spin" color={letra} />}
      {!loading && icon && iconPosition === 'left' && icon}
      {loading ? 'Un momento…' : children}
      {!loading && icon && iconPosition === 'right' && icon}
    </button>
  );
};

export default Button;
